import { FC, JSX, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SensitiveData } from '@ui/Privacy/SensitiveData'

import { calculatePercentage, isNonNegativeNumber } from '@utils/numbers.utils'

import { IntroducingBrokerDetailsDto } from '@model/IntroducingBrokerDetailsDto'
import { LoyaltyDashboardDetailsDto } from '@model/IntroducingBrokerLoyalty'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { ProgressBarsBlock } from '@shared/components/ProgressBarsBlock/ProgressBarsBlock'

interface ProgressBarsProps {
  loyaltyData?: LoyaltyDashboardDetailsDto
  ibData?: IntroducingBrokerDetailsDto
}

const calculateBlockPercentage = (
  overallCurrent: number,
  prevRequired: number,
  currentRequired: number
) => {
  if (overallCurrent >= currentRequired) {
    return 100
  }
  if (overallCurrent <= prevRequired) {
    return 0
  }

  return ((overallCurrent - prevRequired) / (currentRequired - prevRequired)) * 100
}

export const ProgressBars: FC<ProgressBarsProps> = (props) => {
  const { loyaltyData, ibData } = props
  const { formatNumber, compactNumber } = useFormatNumber()

  const { t } = useTranslation()

  const getLabel = useCallback(
    (singularKey: string, amount?: number, pluralKey?: string): string => {
      if (isNonNegativeNumber(amount)) {
        if (pluralKey) {
          return amount === 1 ? `${amount} ${t(singularKey)}` : `${amount} ${t(pluralKey)}`
        }
        return `${amount} ${t(singularKey)}`
      }
      return ''
    },
    [t]
  )

  const getLabelElement = useCallback(
    (singularKey: string, amount?: number, pluralKey?: string): JSX.Element | null => {
      if (!isNonNegativeNumber(amount)) {
        return null
      }

      const unit = pluralKey ? (amount === 1 ? t(singularKey) : t(pluralKey)) : t(singularKey)

      return (
        <>
          <SensitiveData>{amount}</SensitiveData> {unit}
        </>
      )
    },
    [t]
  )

  const currentTier = ibData?.loyaltyDetails?.currentTier
  const currentTierMinNv = currentTier?.minNotionalValue ?? 0
  const currentTierMaxNv = currentTier?.maxNotionalValue ?? 0
  const currentTierNv = currentTier?.bonusNv.current ?? 0
  const nextTier = ibData?.loyaltyDetails?.nextTier

  const notionalValuePercentages = useMemo(() => {
    const loyaltyTiers = ibData?.loyaltyDetails?.tiers

    const getTierWithSubtiersBeforeCurrent = () => {
      // Complete tier like Bronze doesn't have sub-tiers although should they should be used if previous tier required value equals current notional value. Otherwise will show single bar without 4 slices.
      // Once will have at least 1 notional value higher than previous tier then sub-tiers are not required anymore.
      const tierIndexWithSubTiersBeforeCurrent =
        loyaltyTiers?.findIndex((tier, idx) => {
          const previousTier = loyaltyTiers?.[idx - 1]

          // Only used if next tier was started but current notional value didn't exceed previous tier required notional value
          const doesTierBeforeCurrentTierHasSubTiers =
            tier.bonusNv.current === previousTier?.bonusNv.required &&
            (previousTier?.subTiers?.length ?? 0) > 0

          return doesTierBeforeCurrentTierHasSubTiers
        }) ?? -1

      const tierWithSubTiersBeforeCurrent =
        tierIndexWithSubTiersBeforeCurrent > 0
          ? loyaltyTiers?.[tierIndexWithSubTiersBeforeCurrent - 1]
          : undefined

      return tierWithSubTiersBeforeCurrent
    }

    const subTiers = currentTier?.subTiers ?? getTierWithSubtiersBeforeCurrent()?.subTiers

    if (subTiers) {
      let ignoreRestCalculations = false

      return (
        subTiers?.map((tier, idx) => {
          let currentNv
          let requiredNv

          if (!tier.isCurrent) {
            currentNv = tier.bonusNv.current
            requiredNv = tier?.bonusNv.required
          } else if (!nextTier) {
            currentNv = currentTierNv
            requiredNv = currentTierMaxNv
          } else {
            currentNv = currentTierNv - currentTierMinNv
            requiredNv = currentTierMaxNv - currentTierMinNv
          }

          const percent = !ignoreRestCalculations
            ? calculatePercentage(currentNv, requiredNv)
            : null

          if (tier.isCurrent || tier.bonusNv.current === tier.bonusNv.required) {
            ignoreRestCalculations = true
          }

          return {
            percent,
            isCurrent: tier.isCurrent,
            isNextStarted: subTiers[idx + 1]?.bonusNv.current > subTiers[idx + 1]?.minNotionalValue,
          }
        }) ?? []
      )
    }

    const currentNv = !nextTier ? currentTierNv : currentTierNv - currentTierMinNv
    const requiredNv = !nextTier ? currentTierMaxNv : currentTierMaxNv - currentTierMinNv

    return [
      {
        percent: calculatePercentage(currentNv, requiredNv),
        isCurrent: true,
      },
    ]
  }, [
    currentTier?.subTiers,
    currentTierNv,
    currentTierMaxNv,
    currentTierMinNv,
    ibData?.loyaltyDetails?.tiers,
    nextTier,
  ])

  const { current, next } = loyaltyData?.tiers || {}
  const { subTiers, id } = current || {}

  let tierRewardAmount = next?.amount

  if (subTiers?.length) {
    const currentSubTierIndex = subTiers.findIndex(({ id: subTierId }) => subTierId === id)
    const nextSubTierAmount = subTiers[currentSubTierIndex + 1]?.amount

    if (nextSubTierAmount) {
      tierRewardAmount = nextSubTierAmount
    }
  }

  const isFinalTier = loyaltyData?.tiers.final?.id === id
  const prizeText =
    isFinalTier || !tierRewardAmount
      ? null
      : `${compactNumber(currentTier?.bonusNv?.required ?? 0, 4)} - $${t(
          'Loyalty Program.amount_prize',
          {
            amount: tierRewardAmount,
          }
        )}`

  const items = useMemo(() => {
    const activeClientsPercent =
      loyaltyData?.activeClients && loyaltyData?.activeClients.required
        ? calculateBlockPercentage(
            loyaltyData.activeClients.current,
            0,
            loyaltyData.activeClients.required
          )
        : 0

    const volumeRatioPercent =
      loyaltyData?.volumeRatio && loyaltyData?.volumeRatio.required
        ? calculateBlockPercentage(
            loyaltyData.volumeRatio.current,
            0,
            loyaltyData.volumeRatio.required
          )
        : 0

    return [
      {
        title: t('IB.Notional Value'),
        value: (
          <SensitiveData>{compactNumber(currentTier?.bonusNv?.required ?? 0, 4)}</SensitiveData>
        ),
        sensitiveExtraInfo: prizeText,
        progressTooltip: formatNumber(currentTier?.bonusNv?.current ?? 0, {
          minDecimalPlaces: 0,
        }),
        sections: notionalValuePercentages,
      },
      {
        title: t('IB.Dashboard.Active Clients (90 Days)'),
        value: getLabelElement(
          'IB.Dashboard.Client',
          loyaltyData?.activeClients.required,
          'IB.Dashboard.Clients'
        ),
        progressTooltip: getLabel(
          'IB.Dashboard.Client',
          loyaltyData?.activeClients.current,
          'IB.Dashboard.Clients'
        ),
        sections: [
          {
            percent: activeClientsPercent,
            isCurrent: true,
          },
        ],
      },
      {
        title: t('IB.Dashboard.Volume'),
        value: (
          <>
            <SensitiveData>{loyaltyData?.volumeRatio.required}%</SensitiveData>{' '}
            {t('IB.Dashboard.Volume')}
          </>
        ),
        progressTooltip: `${loyaltyData?.volumeRatio.current}% ${t('IB.Dashboard.Volume')}`,
        sections: [
          {
            percent: volumeRatioPercent,
            isCurrent: true,
          },
        ],
      },
    ]
  }, [
    loyaltyData,
    t,
    getLabel,
    getLabelElement,
    notionalValuePercentages,
    currentTier?.bonusNv,
    prizeText,
  ])

  return <ProgressBarsBlock items={items} />
}
